/**
 * sl-9qm.4.1: ComponentRegistry — Registry for Custom App Components
 *
 * SOLID:
 * - S: Only manages component registrations (no rendering, no data)
 * - O: New components can be registered without modifying existing code
 * - D: Consumers depend on ViewComponent interface, not concrete implementations
 *
 * Pattern: Same as FullscreenRegistry (fullscreen-registry.ts)
 * — Service + LiveData for reactive state, Map as registration store.
 */
import { LiveData, Service } from '@toeverything/infra';
import { z } from 'zod';

import type { ViewComponentType } from '../constants/view-component-types';
import {
  type ComponentCapability,
  ComponentCapabilitySchema,
} from '../types/capability-schema';
import type {
  ViewComponent,
  ViewComponentCategory,
} from '../types/view-component.types';

const CapabilityArraySchema = z.array(ComponentCapabilitySchema);

export class ComponentRegistry extends Service {
  private readonly _components = new Map<ViewComponentType, ViewComponent>();

  /** Reactive list of all registered components */
  readonly registeredComponents$ = new LiveData<readonly ViewComponent[]>([]);

  /**
   * Register a ViewComponent.
   * Idempotent — re-registering the same id overwrites.
   * Validates defaultConfig against configSchema on registration.
   */
  register(component: ViewComponent): void {
    // Validate defaultConfig against configSchema
    const result = component.configSchema.safeParse(component.defaultConfig);
    if (!result.success) {
      console.error(
        `[ComponentRegistry] Invalid defaultConfig for "${component.id}":`,
        result.error.issues
      );
      return;
    }

    // sl-8n5m.1.3: Validate capabilities against canonical schema + fill defaults.
    const capResult = CapabilityArraySchema.safeParse(component.capabilities);
    if (!capResult.success) {
      console.error(
        `[ComponentRegistry] Invalid capabilities for "${component.id}":`,
        capResult.error.issues
      );
      return;
    }

    // Store with parsed (defaults-filled) capabilities. Zod 3 `.default()` quirk:
    // runtime value has cardinality filled, but `z.infer` still types it optional —
    // cast to the authoring input type which matches the ViewComponent contract.
    this._components.set(component.id, {
      ...component,
      capabilities:
        capResult.data as ComponentCapability[] as ViewComponent['capabilities'],
    });
    this.registeredComponents$.next(Array.from(this._components.values()));
  }

  /**
   * sl-8n5m.1.3: Returns the component's canonical capabilities, if declared.
   * Returns undefined when the component has not yet declared canonical
   * capabilities and still only exposes `dataBindings` (pre-L3.1.2).
   */
  getCapabilities(id: ViewComponentType): ComponentCapability[] | undefined {
    // Registered `capabilities` is the post-zod runtime shape (required
    // cardinality); the authoring ViewComponent type declares cardinality
    // optional. Cast matches the cast used at register() time.
    return this._components.get(id)?.capabilities as
      | ComponentCapability[]
      | undefined;
  }

  /**
   * sl-8n5m.1.3: All components declaring a capability with the given role.
   */
  listComponentsByRole(role: string): ViewComponent[] {
    return Array.from(this._components.values()).filter(c =>
      (c.capabilities ?? []).some(cap => cap.role === role)
    );
  }

  /**
   * sl-8n5m.1.3: Required roles for a component (capability.required === true).
   */
  getRequiredRolesOf(id: ViewComponentType): string[] {
    const caps = this._components.get(id)?.capabilities ?? [];
    return caps.filter(c => c.required).map(c => c.role);
  }

  /**
   * Get a component by its type identifier.
   */
  get(id: ViewComponentType): ViewComponent | undefined {
    return this._components.get(id);
  }

  /**
   * Get all components of a given category.
   */
  getByType(type: ViewComponentCategory): ViewComponent[] {
    return Array.from(this._components.values()).filter(c => c.type === type);
  }

  /**
   * Get all registered components.
   */
  getAll(): ViewComponent[] {
    return Array.from(this._components.values());
  }
}
